import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'

// Hard-coded operator allowlist. Mirrors the server-side
// ADMIN_EMAIL_ALLOWLIST default in mtg-api `app/api_keys.py`. Both
// gates must agree — adding a new admin requires updating BOTH lists.
const ADMIN_EMAILS = new Set(['coughlka@gmail.com'])

interface Props {
  children: ReactNode
}

/**
 * Route gate that only renders children for whitelisted operator
 * emails. While auth is hydrating, renders a minimal loading state
 * (NOT a redirect) so a hard refresh on the admin route doesn't
 * bounce off to home before useAuth settles.
 *
 * Anyone not whitelisted gets redirected to `/`. There's no error
 * message — keeps the surface invisible to non-operators.
 */
export function RequireKeith({ children }: Props) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 text-sm text-gray-400">
        {/* eslint-disable-next-line i18next/no-literal-string -- the admin gate (English by decision) */}
        {'Loading…'}
      </div>
    )
  }

  if (!user || !user.email || !ADMIN_EMAILS.has(user.email.toLowerCase())) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
